import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  applyAcceptanceReviewLifecycle,
  createAcceptanceReview,
  decideAcceptanceReview,
  exportAcceptanceReview,
  listAcceptanceReviews,
  listBranchAnalyses,
  normalizeError,
  preflightAcceptanceReview,
  resolveAcceptanceReviewCondition,
} from "./api";
import { useAuth } from "./AuthContext";
import { canMutateRepos, VIEWER_READ_ONLY_MESSAGE } from "./orgPermissions";
import { normalizeBranchInput } from "./normalizeBranchInput";
import { groupRequirementResults, requirementScorecard } from "./taskRequirementGroups";
import { customerReviewSummary } from "./taskCustomerCopy";
import "./AcceptanceReviewSection.css";

function shortSha(sha) {
  if (!sha || sha === "unknown") return "—";
  return sha.slice(0, 7);
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

const STATUS_LABELS = {
  met: "Met",
  partially_met: "Partially met",
  cannot_verify: "Cannot verify",
  not_met: "Not met",
};

const IMPLEMENTATION_STATUS_LABELS = {
  implemented: "Implemented, not verified",
  tested: "Tested, not executed",
  verified: "Executed tests, not runtime-verified",
  partially_supported: "Partially supported",
  unverified: "Unverified",
  contradicted: "Contradicted",
  not_implemented: "Not implemented",
};

const DIMENSION_LABELS = {
  implementation: "Implementation",
  tests: "Tests",
  ciExecution: "CI execution",
  runtime: "Runtime",
  human: "Human",
};

function statusLabel(status) {
  return STATUS_LABELS[status] || status || "Unknown";
}

function FileListDetails({ label, files }) {
  if (!files?.length) return null;
  return (
    <details className="acceptance-review-details" data-testid={`acceptance-review-details-${label}`}>
      <summary>
        {label} ({files.length})
      </summary>
      <ul>
        {files.map((path) => (
          <li key={path}>
            <code>{path}</code>
          </li>
        ))}
      </ul>
    </details>
  );
}

function EvidenceList({ label, items }) {
  if (!items?.length) return null;
  return (
    <details className="acceptance-review-details">
      <summary>
        {label} ({items.length})
      </summary>
      <ul>
        {items.map((item) => (
          <li key={item.evidenceId || `${item.kind}-${item.path}-${item.label}`}>
            <code>{item.path || item.evidenceId}</code>
            {item.newStart ? (
              <span>
                {" "}
                lines {item.newStart}
                {item.newEnd ? `–${item.newEnd}` : ""}
              </span>
            ) : null}
            {item.label ? <span> &mdash; {item.label}</span> : null}
            {item.detail ? <span> &mdash; {item.detail}</span> : null}
            {item.freshness ? (
              <span className="acceptance-review-evidence-meta"> ({item.freshness})</span>
            ) : null}
            {item.githubDiffUrl ? (
              <a
                href={item.githubDiffUrl}
                target="_blank"
                rel="noreferrer"
                className="acceptance-review-diff-link"
              >
                Open GitHub diff
              </a>
            ) : null}
          </li>
        ))}
      </ul>
    </details>
  );
}

function FounderEvidenceCard({ card }) {
  if (!card) return null;
  const canShowCode = Boolean(card.strongestEvidence?.length);
  return (
    <article
      className={`acceptance-review-evidence-card result-${String(card.resultLabel || "")
        .toLowerCase()
        .replace(/\s+/g, "-")}`}
      data-testid="acceptance-review-evidence-card"
    >
      <header>
        <h6 data-testid="acceptance-review-evidence-requirement">{card.requirement}</h6>
        <p
          className="acceptance-review-evidence-result"
          data-testid="acceptance-review-evidence-result"
          role="status"
        >
          {card.resultLabel}
        </p>
      </header>
      <p data-testid="acceptance-review-evidence-conclusion">{card.conclusion}</p>
      <p data-testid="acceptance-review-evidence-why">
        <strong>Why RenderRepo says this:</strong> {card.why}
      </p>
      {card.strongestEvidence?.length ? (
        <ul data-testid="acceptance-review-evidence-refs">
          {card.strongestEvidence.map((item) => {
            const isAbsence = item.changeKind === "absence_probe" || item.strength === "Missing at this revision";
            return (
              <li key={`${item.path}-${item.newStart || "file"}-${item.label}`}>
                <code>{item.path}</code>
                {item.hasExactLines && item.newStart ? (
                  <span>
                    {" "}
                    lines {item.newStart}
                    {item.newEnd ? `–${item.newEnd}` : ""}
                  </span>
                ) : isAbsence ? (
                  <span> — inspected; required statement not found</span>
                ) : (
                  <span> — file-level evidence only</span>
                )}
                <span className="acceptance-review-evidence-meta"> ({item.strength})</span>
                {item.excerpt ? (
                  <p className="acceptance-review-evidence-excerpt">{item.excerpt}</p>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {card.missingProof?.length ? (
        <p data-testid="acceptance-review-evidence-missing">
          <strong>Missing proof:</strong> {card.missingProof.join("; ")}
        </p>
      ) : null}
      <p data-testid="acceptance-review-evidence-next">
        <strong>Next:</strong> {card.nextAction}
      </p>
      {canShowCode ? (
        <details className="acceptance-review-details">
          <summary>Show the code</summary>
          <ul>
            {card.strongestEvidence.map((item) => {
              const isAbsence =
                item.changeKind === "absence_probe" || item.strength === "Missing at this revision";
              return (
                <li key={`code-${item.path}-${item.newStart || 0}-${item.label}`}>
                  <div>
                    <code>{item.path}</code>
                    {item.pinnedCommitSha ? (
                      <span className="acceptance-review-evidence-meta">
                        {" "}
                        @ {String(item.pinnedCommitSha).slice(0, 7)}
                      </span>
                    ) : null}
                  </div>
                  <div>{item.label}</div>
                  {item.hasExactLines ? (
                    <div>
                      Lines {item.newStart}
                      {item.newEnd ? `–${item.newEnd}` : ""}
                    </div>
                  ) : isAbsence ? (
                    <div>Validated absence probe — no fabricated line number.</div>
                  ) : (
                    <div>File-level evidence only. Exact lines were not available.</div>
                  )}
                  {item.excerpt ? <pre className="acceptance-review-evidence-pre">{item.excerpt}</pre> : null}
                  {item.relevance ? (
                    <div className="acceptance-review-evidence-meta">Relevance: {item.relevance}</div>
                  ) : null}
                  {item.githubDiffUrl ? (
                    <a
                      href={item.githubDiffUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="acceptance-review-diff-link"
                    >
                      View on GitHub
                    </a>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}
    </article>
  );
}

function StringListDetails({ label, items }) {
  if (!items?.length) return null;
  return (
    <details className="acceptance-review-details">
      <summary>
        {label} ({items.length})
      </summary>
      <ul>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </details>
  );
}

const HANDOVER_LABELS = {
  approve: "Looks ready to approve",
  approve_conditionally: "More proof needed before approval",
  do_not_approve: "Changes required",
  rerun: "Review the work again",
  already_decided: "A decision is already recorded",
};

const CI_EVIDENCE_LABELS = {
  passing: "Automated tests passed on the reviewed revision.",
  failing: "Tests failed on the reviewed revision.",
  pending: "Tests have not finished on the reviewed revision.",
  untrusted: "A human or untrusted claim about tests was ignored. Only machine-run results count.",
  unavailable: "Test results could not be retrieved for this revision.",
  absent: "Tests have not produced a result for this revision yet.",
};

function describeCiEvidence(ci) {
  if (!ci) return "Tests are not connected for this review.";
  if (ci.policy === "unavailable" || ci.collection === "disabled") {
    return "Tests are not connected for this repository.";
  }
  if (ci.status === "absent" && ci.policy === "required") {
    return "Required tests have not produced a result for this revision yet. Re-run the review after tests finish.";
  }
  return CI_EVIDENCE_LABELS[ci.status] || CI_EVIDENCE_LABELS.absent;
}

const ROUTING_OWNER_LABELS = {
  product: "Product",
  engineering: "Engineering",
  evidence: "Evidence",
  control_owner: "Control owner",
};

const ROUTING_CLASS_LABELS = {
  product_decision_gap: "a decision is still open",
  engineering_implementation_gap: "the code does not satisfy it",
  evidence_gap: "we could not prove it either way",
  policy_control_gap: "we lack the control to decide safely",
};

const COVERAGE_LABELS = {
  proven: "proven",
  partially_proven: "partly proven",
  contradicted: "evidence disagrees",
  missing: "no evidence",
  stale: "evidence is stale",
  untrusted: "evidence not trusted",
};

/**
 * Per-requirement view. Deliberately shows the verdict and the evidence coverage
 * as two separate columns: a criterion can read "met" from a code read while the
 * tests that would prove it never ran, and collapsing those into one status is
 * how a review starts overstating what it knows.
 */
function RequirementsPanel({ trace, coverage, routing, divergence }) {
  const requirements = trace?.requirements || [];
  if (requirements.length === 0) return null;

  const coverageById = new Map((coverage?.coverage || []).map((entry) => [entry.requirementId, entry]));
  const routingById = new Map((routing?.dispositions || []).map((entry) => [entry.requirementId, entry]));

  return (
    <section
      className="acceptance-review-requirements"
      data-testid="acceptance-review-requirements"
      aria-labelledby="acceptance-review-requirements-heading"
    >
      <h6 id="acceptance-review-requirements-heading">Requirements</h6>

      {trace.summary?.derived ? (
        <p className="acceptance-review-requirements-note" data-testid="acceptance-review-requirements-derived">
          This review was created before requirements carried stable identities, so these were
          reconstructed from the frozen criteria text and cannot be traced back to a product decision.
        </p>
      ) : null}
      {trace.summary?.unattributedResults > 0 ? (
        <p className="acceptance-review-requirements-warn" data-testid="acceptance-review-requirements-unattributed">
          {trace.summary.unattributedResults} evaluation result(s) could not be matched to a
          requirement and are not shown below.
        </p>
      ) : null}
      {routing && routing.productLinkageAvailable === false ? (
        <p className="acceptance-review-requirements-warn" data-testid="acceptance-review-requirements-linkage">
          {routing.unattributedOpenQuestions} open product question(s) could not be linked to a
          specific requirement, so a product gap may be shown below under a different owner.
        </p>
      ) : null}
      {divergence && divergence.kind === "graded_newer_than_delivered" ? (
        <p className="acceptance-review-requirements-warn" data-testid="acceptance-review-requirements-divergence">
          This review grades contract version {divergence.gradedVersion}, but engineering took
          delivery of version {divergence.deliveredVersion}.{" "}
          {divergence.blameShieldedRequirementIds?.length
            ? `${divergence.blameShieldedRequirementIds.length} requirement(s) changed after the handoff and are shown as product decisions rather than implementation gaps.`
            : "Which requirements changed is unknown, because the delivered set was not recorded."}
        </p>
      ) : null}
      {divergence && divergence.kind === "graded_older_than_delivered" ? (
        <p className="acceptance-review-requirements-warn" data-testid="acceptance-review-requirements-divergence">
          Engineering took delivery of contract version {divergence.deliveredVersion}, which is newer
          than the version {divergence.gradedVersion} being graded here. The work may have been done
          against requirements this review is not checking.
        </p>
      ) : null}
      {divergence && divergence.kind === "no_delivery_record" ? (
        <p className="acceptance-review-requirements-note" data-testid="acceptance-review-requirements-divergence-unknown">
          No record shows which handoff version engineering received, so whether these are the
          requirements the work was done against is unknown.
        </p>
      ) : null}

      <ul className="acceptance-review-requirement-list">
        {requirements.map((requirement) => {
          const requirementCoverage = coverageById.get(requirement.requirementId);
          const disposition = routingById.get(requirement.requirementId);
          return (
            <li
              key={requirement.requirementId}
              className="acceptance-review-requirement"
              data-testid={`acceptance-review-requirement-${requirement.requirementId}`}
            >
              <p className="acceptance-review-requirement-text">{requirement.text}</p>
              <dl className="acceptance-review-requirement-meta">
                <div>
                  <dt>Review verdict</dt>
                  <dd data-testid={`requirement-status-${requirement.requirementId}`}>
                    {String(requirement.status).replace(/_/g, " ")}
                  </dd>
                </div>
                <div>
                  <dt>Evidence</dt>
                  <dd data-testid={`requirement-coverage-${requirement.requirementId}`}>
                    {COVERAGE_LABELS[requirementCoverage?.state] || "not assessed"}
                  </dd>
                </div>
              </dl>
              {requirementCoverage?.verdictExceedsEvidence ? (
                <p
                  className="acceptance-review-requirement-warn"
                  data-testid={`requirement-overclaim-${requirement.requirementId}`}
                >
                  {requirementCoverage.rationale}
                </p>
              ) : null}
              {disposition ? (
                <p
                  className="acceptance-review-requirement-routing"
                  data-testid={`requirement-routing-${requirement.requirementId}`}
                >
                  <strong>Goes to {ROUTING_OWNER_LABELS[disposition.owner] || disposition.owner}</strong>
                  {" — "}
                  {ROUTING_CLASS_LABELS[disposition.routingClass] || disposition.routingClass}.{" "}
                  {disposition.rationale} Suggested next step:{" "}
                  {String(disposition.proposedAction).replace(/_/g, " ")}.
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function HandoverReport({ handover }) {
  if (!handover) return null;
  const recommendation = handover.recommendation || "do_not_approve";
  const conditions = Array.isArray(handover.conditions) ? handover.conditions : [];
  const blockers = Array.isArray(handover.blockers) ? handover.blockers : [];
  const residualRisks = Array.isArray(handover.residualRisks) ? handover.residualRisks : [];
  const checks = Array.isArray(handover.requiredHumanChecks)
    ? handover.requiredHumanChecks
    : [];

  return (
    <div
      className={`acceptance-review-handover recommendation-${recommendation}`}
      data-testid="acceptance-review-handover"
    >
      <div className="acceptance-review-handover-header">
        <span
          className={`acceptance-review-handover-badge recommendation-${recommendation}`}
          data-testid="acceptance-review-handover-recommendation"
        >
          {HANDOVER_LABELS[recommendation] || recommendation}
        </span>
        <h6 data-testid="acceptance-review-handover-headline">{handover.headline}</h6>
      </div>
      <p data-testid="acceptance-review-handover-summary">{handover.summary}</p>

      {blockers.length > 0 ? (
        <div data-testid="acceptance-review-handover-blockers">
          <h6>What blocks approval</h6>
          <ul>
            {blockers.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {residualRisks.length > 0 ? (
        <div data-testid="acceptance-review-handover-risks">
          <h6>Residual risks</h6>
          <ul>
            {residualRisks.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {checks.length > 0 ? (
        <div data-testid="acceptance-review-handover-checks">
          <h6>Required human checks</h6>
          <ul>
            {checks.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {conditions.length > 0 ? (
        <details className="acceptance-review-details" data-testid="acceptance-review-handover-conditions">
          <summary>Condition checklist ({conditions.filter((item) => item.passed).length}/{conditions.length} passed)</summary>
          <ul className="acceptance-review-handover-conditions">
            {conditions.map((item) => (
              <li key={item.id} data-passed={item.passed ? "true" : "false"}>
                <strong>{item.passed ? "Pass" : "Fail"}</strong> — {item.label}: {item.detail}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function CriterionCard({ result }) {
  const supportCount = result.supportingEvidence?.length ?? 0;
  const contradictCount = result.contradictingEvidence?.length ?? 0;
  const missingCount = result.missingEvidence?.length ?? 0;
  const stepsCount = result.verificationSteps?.length ?? 0;
  const awareness = result.implementationAwareness;
  const implementationStatus = awareness?.status;

  return (
    <details
      className={`acceptance-review-criterion-card status-${result.status}`}
      data-testid="acceptance-review-criterion-card"
    >
      <summary className="acceptance-review-criterion-header">
        <span
          className={`acceptance-review-status-badge status-${result.status}`}
          data-testid="acceptance-review-status-badge"
        >
          {statusLabel(result.status)}
        </span>
        {implementationStatus ? (
          <span
            className={`acceptance-review-implementation-badge status-${implementationStatus}`}
            data-testid="acceptance-review-implementation-badge"
          >
            {IMPLEMENTATION_STATUS_LABELS[implementationStatus] || implementationStatus}
          </span>
        ) : null}
        <span className="acceptance-review-confidence">Confidence: {result.confidence}</span>
        <p className="acceptance-review-criterion-text">{result.displayText || result.criterionText}</p>
      </summary>
      <p className="acceptance-review-criterion-summary">
        {awareness?.explanation || result.summary}
      </p>
      {awareness?.dimensions ? (
        <ul className="acceptance-review-evidence-dimensions" data-testid="acceptance-review-evidence-dimensions">
          {Object.entries(DIMENSION_LABELS).map(([key, label]) => (
            <li key={key} data-state={awareness.dimensions[key] || "absent"}>
              {label}: {awareness.dimensions[key] || "absent"}
            </li>
          ))}
        </ul>
      ) : null}
      {awareness?.humanReviewQuestion ? (
        <p className="acceptance-review-human-question" data-testid="acceptance-review-human-question">
          Review question: {awareness.humanReviewQuestion}
        </p>
      ) : null}
      <p className="acceptance-review-criterion-counts">
        Evidence {supportCount + contradictCount} &middot; Missing {missingCount} &middot; Steps {stepsCount}
      </p>
      <details className="acceptance-review-details">
        <summary>Exact evidence</summary>
        <EvidenceList label="Supporting evidence" items={result.supportingEvidence} />
        <EvidenceList label="Contradicting evidence" items={result.contradictingEvidence} />
        <StringListDetails label="Missing evidence" items={result.missingEvidence} />
        <StringListDetails label="Limitations" items={awareness?.limitations} />
        <StringListDetails label="Verification steps" items={result.verificationSteps} />
        {awareness?.githubDiffUrl ? (
          <p>
            <a
              href={awareness.githubDiffUrl}
              target="_blank"
              rel="noreferrer"
              className="acceptance-review-diff-link"
            >
              Open corresponding GitHub diff
            </a>
          </p>
        ) : null}
      </details>
    </details>
  );
}

function PreflightPanel({ preflight }) {
  if (!preflight) return null;
  const readyClass = preflight.ready ? "preflight-ready" : "preflight-not-ready";
  return (
    <div className={`acceptance-review-preflight ${readyClass}`} data-testid="acceptance-review-preflight">
      <h6>Preflight check</h6>
      <dl className="acceptance-review-meta">
        <div>
          <dt>Repository:</dt>
          <dd>{preflight.repository}</dd>
        </div>
        <div>
          <dt>Branch:</dt>
          <dd>{preflight.normalizedBranch}</dd>
        </div>
        <div>
          <dt>Base branch:</dt>
          <dd>{preflight.baseBranch}</dd>
        </div>
        <div>
          <dt>Analysis status:</dt>
          <dd>{preflight.analysisStatus}</dd>
        </div>
        <div>
          <dt>Analysis ID:</dt>
          <dd><code>{shortSha(preflight.analysisRepoId)}</code></dd>
        </div>
        {preflight.indexedBranch && (
          <div>
            <dt>Indexed branch:</dt>
            <dd>{preflight.indexedBranch}</dd>
          </div>
        )}
        {preflight.indexedCommitSha && (
          <div>
            <dt>Indexed commit:</dt>
            <dd><code>{shortSha(preflight.indexedCommitSha)}</code></dd>
          </div>
        )}
        <div>
          <dt>Evidence match:</dt>
          <dd>{preflight.matchQuality === "exact" ? "Matched" : "No match"}</dd>
        </div>
        <div>
          <dt>Evidence ready:</dt>
          <dd>{preflight.evidenceReady ? "Yes" : "No"}</dd>
        </div>
        <div>
          <dt>Status:</dt>
          <dd className={readyClass} data-testid="preflight-status">
            {preflight.ready ? "Ready" : "Needs attention"}
          </dd>
        </div>
      </dl>
      {preflight.message && !preflight.ready && (
        <p className="acceptance-review-preflight-message">{preflight.message}</p>
      )}
    </div>
  );
}

export default function AcceptanceReviewSection({
  featurePlanId,
  planStatus,
  planTitle,
  repoId,
  onLatestReviewChange,
  heading = "Delivery Review",
  intro,
  reviewEligible = false,
  decisionAllowed = null,
  initialReview = null,
  hideSourceForm = false,
}) {
  const { user } = useAuth();
  const mutateAllowed = canMutateRepos(user);
  const [branchInput, setBranchInput] = useState("");
  const [baseInput, setBaseInput] = useState("");
  const [normalizedBranch, setNormalizedBranch] = useState("");
  const [normalizedBase, setNormalizedBase] = useState("");
  const [branchError, setBranchError] = useState("");
  const [reviews, setReviews] = useState(() => (initialReview ? [initialReview] : []));
  const [loadingList, setLoadingList] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [runState, setRunState] = useState("idle");
  const [preflight, setPreflight] = useState(null);
  const [preflighting, setPreflighting] = useState(false);
  const [branchAnalyses, setBranchAnalyses] = useState([]);
  const [useManualInput, setUseManualInput] = useState(false);
  const [decisionNote, setDecisionNote] = useState("");
  const [conditionsDraft, setConditionsDraft] = useState("");
  const [conditionNotes, setConditionNotes] = useState({});
  const [deciding, setDeciding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [exportError, setExportError] = useState("");
  const [selectedAnalysisId, setSelectedAnalysisId] = useState("");
  const [headSha, setHeadSha] = useState("");
  const [baseSha, setBaseSha] = useState("");
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const decisionRegionRef = useRef(null);
  const errorRef = useRef(null);
  const summaryRef = useRef(null);
  const canReview = planStatus === "completed" || reviewEligible === true;

  const focusAfterAction = useCallback((kind) => {
    const target =
      kind === "error"
        ? errorRef.current
        : kind === "summary"
          ? summaryRef.current
          : decisionRegionRef.current;
    if (target && typeof target.focus === "function") {
      target.focus({ preventScroll: false });
    }
  }, []);

  useEffect(() => {
    if (!repoId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await listBranchAnalyses(repoId);
        if (!cancelled) {
          const analyses = data.analyses || [];
          setBranchAnalyses(analyses);
          if (analyses.length === 0) {
            setUseManualInput(true);
          } else {
            const pinned = analyses.find((a) => a.commitSha && /^[0-9a-f]{40}$/i.test(a.commitSha));
            const defaultMatch = pinned || analyses[0];
            if (defaultMatch) {
              setSelectedAnalysisId(defaultMatch.repoId);
              setNormalizedBranch(defaultMatch.branch);
              setBranchInput(defaultMatch.branch);
              setHeadSha(defaultMatch.commitSha || "");
              setBaseSha(defaultMatch.baseSha || "");
            }
          }
        }
      } catch {
        if (!cancelled) setUseManualInput(true);
      }
    })();
    return () => { cancelled = true; };
  }, [repoId]);

  const handleBranchSelect = (e) => {
    const value = e.target.value;
    if (value === "__manual__") {
      setUseManualInput(true);
      setSelectedAnalysisId("");
      setBranchInput("");
      setNormalizedBranch("");
      setHeadSha("");
      setBaseSha("");
      setPreflight(null);
      return;
    }
    const analysis = branchAnalyses.find((item) => item.repoId === value);
    setUseManualInput(false);
    setSelectedAnalysisId(value);
    setBranchInput(analysis?.branch || "");
    setNormalizedBranch(analysis?.branch || "");
    setHeadSha(analysis?.commitSha || "");
    setBaseSha(analysis?.baseSha || "");
    setBranchError("");
    setPreflight(null);
  };

  const loadReviews = useCallback(async () => {
    if (!featurePlanId || !canReview) return;
    setLoadingList(true);
    setError("");
    try {
      const data = await listAcceptanceReviews(featurePlanId);
      const next = Array.isArray(data.reviews) ? data.reviews : [];
      setReviews(next);
    } catch (err) {
      const normalized = normalizeError(err);
      setError(normalized.message || "Failed to load acceptance reviews");
    } finally {
      setLoadingList(false);
    }
  }, [featurePlanId, canReview]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  useEffect(() => {
    if (typeof onLatestReviewChange !== "function") return;
    onLatestReviewChange(reviews[0] || null);
  }, [reviews, onLatestReviewChange]);

  const handleBranchChange = (e) => {
    const raw = e.target.value;
    setBranchInput(raw);
    setBranchError("");
    setPreflight(null);

    if (!raw.trim()) {
      setNormalizedBranch("");
      return;
    }
    const result = normalizeBranchInput(raw);
    if (result.ok) {
      setNormalizedBranch(result.branch);
    } else {
      setNormalizedBranch("");
      setBranchError(result.message);
    }
  };

  const handleBaseChange = (e) => {
    const raw = e.target.value;
    setBaseInput(raw);
    setPreflight(null);

    if (!raw.trim()) {
      setNormalizedBase("");
      return;
    }
    const result = normalizeBranchInput(raw);
    if (result.ok) {
      setNormalizedBase(result.branch);
    } else {
      setNormalizedBase("");
    }
  };

  const handlePreflight = async () => {
    if (!normalizedBranch || preflighting) return;
    setPreflighting(true);
    setError("");
    setPreflight(null);
    try {
      const data = await preflightAcceptanceReview(featurePlanId, {
        branchName: normalizedBranch,
        baseRef: normalizedBase || undefined,
        ...(headSha ? { headSha } : {}),
        ...(baseSha ? { baseSha } : {}),
        ...(selectedAnalysisId ? { analysisRepoId: selectedAnalysisId } : {}),
      });
      setPreflight(data.preflight);
    } catch (err) {
      const normalized = normalizeError(err);
      setError(normalized.message || "Preflight check failed");
    } finally {
      setPreflighting(false);
    }
  };

  const handleRunReview = async (event) => {
    event.preventDefault();
    if (running || !mutateAllowed) return;
    if (!normalizedBranch) {
      setError("Branch name is required");
      return;
    }
    if (!headSha || !/^[0-9a-f]{40}$/i.test(headSha)) {
      setError("The exact delivered version is required. Analyze that version or pull request first.");
      return;
    }
    if (branchError) {
      setError(branchError);
      return;
    }
    setRunning(true);
    setRunState("running");
    setError("");
    try {
      const data = await createAcceptanceReview(featurePlanId, {
        branchName: normalizedBranch,
        ...(normalizedBase ? { baseRef: normalizedBase } : {}),
        headSha,
        ...(baseSha ? { baseSha } : {}),
        ...(selectedAnalysisId ? { analysisRepoId: selectedAnalysisId } : {}),
      });
      if (data.review) {
        setReviews((prev) => [data.review, ...prev.filter((r) => r._id !== data.review._id)]);
        setRunState(data.review.status === "failed" ? "failed" : "complete");
      } else {
        await loadReviews();
        setRunState("complete");
      }
    } catch (err) {
      const normalized = normalizeError(err);
      setError(
        normalized.message ||
          "Acceptance Review could not be completed. Try again after repo indexing finishes."
      );
      setRunState("failed");
    } finally {
      setRunning(false);
    }
  };

  const introText =
    intro ||
    "This review checks whether the delivered work matches the agreed Task. A person still records the final decision.";

  if (!canReview) {
    return (
      <section className="acceptance-review-section" data-testid="acceptance-review-section">
        <h5>{heading}</h5>
        <p className="acceptance-review-disabled">
          Delivery Review is available after engineering accepts the Task, or once planning has completed.
        </p>
      </section>
    );
  }

  const latest = reviews[0];
  const criteria = latest?.criteriaEvaluation?.results || [];
  const groupedCriteria = groupRequirementResults(criteria);
  const scorecard = requirementScorecard(criteria);
  const aggregate = latest?.aggregate;
  const showBranchForm = mutateAllowed && !loadingList && (!latest || showRevisionForm);
  const qualityWarnings = latest?.evidenceQuality?.warnings || [];

  const parsedConditions = (() => {
    const lines = conditionsDraft
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    const parsed = [];
    for (const line of lines) {
      const parts = line.split("|").map((part) => part.trim());
      if (parts.length < 3 || !parts[0] || !parts[1] || !parts[2]) {
        return { error: "Each condition must be: description | owner | due YYYY-MM-DD", items: [] };
      }
      parsed.push({
        description: parts[0],
        owner: parts[1],
        dueAt: parts[2],
      });
    }
    return { error: "", items: parsed };
  })();

  const handleDecision = async (event) => {
    if (!latest?._id || deciding || !mutateAllowed) return;
    if (event === "HUMAN_ACCEPT_CONDITIONAL" && !decisionNote.trim()) {
      setError("A decision note is required for conditional acceptance.");
      focusAfterAction("error");
      return;
    }
    if (event === "HUMAN_ACCEPT_CONDITIONAL" && parsedConditions.error) {
      setError(parsedConditions.error);
      focusAfterAction("error");
      return;
    }
    if (event === "HUMAN_ACCEPT_CONDITIONAL" && parsedConditions.items.length === 0) {
      setError("Conditional acceptance requires at least one tracked condition.");
      focusAfterAction("error");
      return;
    }
    setDeciding(true);
    setError("");
    try {
      const data = await decideAcceptanceReview(latest._id, {
        event,
        note: decisionNote.trim() || undefined,
        conditions: event === "HUMAN_ACCEPT_CONDITIONAL" ? parsedConditions.items : undefined,
      });
      if (data.review) {
        setReviews((prev) => [data.review, ...prev.filter((r) => r._id !== data.review._id)]);
      } else {
        await loadReviews();
      }
      setDecisionNote("");
      focusAfterAction("summary");
    } catch (err) {
      const normalized = normalizeError(err);
      setError(normalized.message || "Could not record acceptance decision");
      focusAfterAction("error");
    } finally {
      setDeciding(false);
    }
  };

  const handleLifecycle = async (event) => {
    if (!latest?._id || deciding || !mutateAllowed) return;
    setDeciding(true);
    setError("");
    try {
      const data = await applyAcceptanceReviewLifecycle(latest._id, { event });
      const next = data.replacement || data.review;
      if (next) {
        setReviews((prev) => [next, ...prev.filter((r) => r._id !== next._id && r._id !== latest._id)]);
      } else {
        await loadReviews();
      }
      focusAfterAction("summary");
    } catch (err) {
      const normalized = normalizeError(err);
      setError(normalized.message || "Could not update acceptance review lifecycle");
      focusAfterAction("error");
    } finally {
      setDeciding(false);
    }
  };

  const handleResolveCondition = async (conditionId, resolution) => {
    if (!latest?._id || deciding || !mutateAllowed) return;
    const note = conditionNotes[conditionId]?.trim();
    if (resolution === "waived" && !note) {
      setError("Waiving a condition requires a written rationale.");
      focusAfterAction("error");
      return;
    }
    setDeciding(true);
    setError("");
    try {
      const data = await resolveAcceptanceReviewCondition(latest._id, conditionId, {
        resolution,
        note: note || undefined,
      });
      if (data.review) {
        setReviews((prev) => [data.review, ...prev.filter((r) => r._id !== data.review._id)]);
      } else {
        await loadReviews();
      }
      focusAfterAction("summary");
    } catch (err) {
      const normalized = normalizeError(err);
      setError(normalized.message || "Could not record condition resolution");
      focusAfterAction("error");
    } finally {
      setDeciding(false);
    }
  };

  const handleExport = async () => {
    if (!latest?._id || exporting) return;
    setExporting(true);
    setExportError("");
    try {
      const data = await exportAcceptanceReview(latest._id);
      setExportResult(data.export?.payload || data.export || null);
    } catch (err) {
      const normalized = normalizeError(err);
      setExportResult(null);
      setExportError(normalized.message || "Could not export this review");
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="acceptance-review-section" data-testid="acceptance-review-section">
      <header className="acceptance-review-header">
        <h5>{heading}</h5>
        {planTitle ? <p className="acceptance-review-plan-title">{planTitle}</p> : null}
        <p className="acceptance-review-intro">{introText}</p>
      </header>

      {latest && !showRevisionForm ? (
        <div className="acceptance-review-source-identity" data-testid="acceptance-review-source-identity">
          <p data-testid="acceptance-review-source-summary">
            {latest.reviewSource?.pullRequestNumber
              ? `PR #${latest.reviewSource.pullRequestNumber}`
              : latest.reviewSource?.branchName || "Delivered revision"}
            {latest.reviewSource?.baseRef && latest.reviewSource?.branchName
              ? ` · ${latest.reviewSource.baseRef} → ${latest.reviewSource.branchName}`
              : ""}
          </p>
          <p className="acceptance-review-muted">
            {latest.branchEvidence?.totalChangedFiles != null
              ? `${latest.branchEvidence.totalChangedFiles} files changed`
              : "The reviewed work is on file."}
          </p>
          {mutateAllowed ? (
            <button
              type="button"
              className="secondary-button"
              data-testid="acceptance-review-revise-source"
              onClick={() => setShowRevisionForm(true)}
            >
              Review different work
            </button>
          ) : null}
        </div>
      ) : null}

      {showBranchForm && !hideSourceForm ? (
        <form className="acceptance-review-form" onSubmit={handleRunReview}>
          <label>
            Branch
            {branchAnalyses.length > 0 && !useManualInput ? (
              <>
                <select
                  value={selectedAnalysisId || ""}
                  onChange={handleBranchSelect}
                  disabled={running}
                  data-testid="acceptance-review-branch-select"
                >
                  <option value="">Select analyzed commit...</option>
                  {branchAnalyses.map((b) => (
                    <option key={b.repoId} value={b.repoId}>
                      {b.branch} @ {shortSha(b.commitSha)} ({b.status})
                    </option>
                  ))}
                  <option value="__manual__">Enter manually...</option>
                </select>
              </>
            ) : (
              <>
                <input
                  type="text"
                  value={branchInput}
                  onChange={handleBranchChange}
                  placeholder="feature/example or https://github.com/owner/repo/tree/feature/example"
                  disabled={running}
                  data-testid="acceptance-review-branch-input"
                />
                {branchAnalyses.length > 0 && (
                  <button
                    type="button"
                    className="acceptance-review-switch-link"
                    onClick={() => { setUseManualInput(false); setBranchInput(""); setNormalizedBranch(""); setBranchError(""); setPreflight(null); }}
                  >
                    Back to branch list
                  </button>
                )}
              </>
            )}
            {normalizedBranch && normalizedBranch !== branchInput.trim() && useManualInput && (
              <span className="acceptance-review-normalized" data-testid="acceptance-review-normalized-branch">
                Branch: <code>{normalizedBranch}</code>
              </span>
            )}
            {branchError && (
              <span className="acceptance-review-field-error" data-testid="acceptance-review-branch-error">
                {branchError}
              </span>
            )}
          </label>
          <label>
            Base branch (optional &mdash; defaults to repository default)
            {branchAnalyses.length > 0 ? (
              <select
                value={normalizedBase || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setBaseInput(val);
                  setNormalizedBase(val);
                  setPreflight(null);
                }}
                disabled={running}
                data-testid="acceptance-review-base-input"
              >
                <option value="">Default ({branchAnalyses[0]?.defaultBranch || "main"})</option>
                {branchAnalyses.map((b) => (
                  <option key={b.repoId} value={b.branch}>
                    {b.branch} ({b.status})
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={baseInput}
                onChange={handleBaseChange}
                placeholder="main"
                disabled={running}
                data-testid="acceptance-review-base-input"
              />
            )}
            {normalizedBase && normalizedBase !== baseInput.trim() && (
              <span className="acceptance-review-normalized" data-testid="acceptance-review-normalized-base">
                Base: <code>{normalizedBase}</code>
              </span>
            )}
          </label>
          <label>
            Exact delivered version
            <input
              type="text"
              value={headSha}
              onChange={(e) => {
                setHeadSha(e.target.value.trim());
                setPreflight(null);
              }}
              placeholder="Paste the exact version id from GitHub"
              disabled={running}
              spellCheck={false}
              data-testid="acceptance-review-head-sha"
            />
          </label>
          <label>
            Comparison starting version
            <input
              type="text"
              value={baseSha}
              onChange={(e) => {
                setBaseSha(e.target.value.trim());
                setPreflight(null);
              }}
              placeholder="Optional starting version id"
              disabled={running}
              spellCheck={false}
              data-testid="acceptance-review-base-sha"
            />
          </label>
          <p className="acceptance-review-muted" data-testid="acceptance-review-sha-missing">
            A branch name is not enough. RenderRepo must review one exact delivered version.
          </p>

          <div className="acceptance-review-actions">
            <button
              type="button"
              className="secondary-button"
              disabled={!normalizedBranch || preflighting || running || !!branchError}
              onClick={handlePreflight}
              data-testid="acceptance-review-preflight-button"
            >
              {preflighting ? "Checking..." : "Check this version"}
            </button>
            <button
              type="submit"
              className="primary-button"
              disabled={running || !!branchError || !headSha || !/^[0-9a-f]{40}$/i.test(headSha)}
              data-testid="acceptance-review-run-button"
            >
              {running
                ? "Running..."
                : runState === "complete"
                  ? "Review complete — run again"
                  : runState === "failed"
                    ? "Review failed — try again"
                    : "Run review"}
            </button>
          </div>
          {latest ? (
            <p className="acceptance-review-muted" data-testid="acceptance-review-revision-warning">
              Reviewing a different revision will make the current findings historical. They stay on
              the record but will no longer describe this delivery.
            </p>
          ) : null}
        </form>
      ) : !mutateAllowed && !latest ? (
        <p className="acceptance-review-readonly" data-testid="acceptance-review-readonly">
          {VIEWER_READ_ONLY_MESSAGE}
        </p>
      ) : null}

      <PreflightPanel preflight={preflight} />

      {error && (
        <p
          className="acceptance-review-error"
          role="alert"
          tabIndex={-1}
          ref={errorRef}
          data-testid="acceptance-review-error"
        >
          {error}
        </p>
      )}

      {loadingList && !latest && (
        <p className="acceptance-review-loading">Loading reviews…</p>
      )}

      {!loadingList && !latest && (
        <p className="acceptance-review-empty" data-testid="acceptance-review-empty">
          This work has not been reviewed yet.
        </p>
      )}

      {latest && (
        <div
          className="acceptance-review-summary"
          data-testid="acceptance-review-summary"
          tabIndex={-1}
          ref={summaryRef}
          aria-live="polite"
          aria-atomic="true"
        >
          {(() => {
            const plain = customerReviewSummary(latest);
            return (
              <div className="acceptance-review-plain" data-testid="acceptance-review-plain">
                <p
                  className="acceptance-review-human-decision"
                  data-testid="acceptance-review-human-decision"
                >
                  <strong>Human decision:</strong> {plain.humanDecisionLabel}
                </p>
                <h6>Review result</h6>
                <p
                  data-testid="acceptance-review-plain-recommendation"
                  role="status"
                  aria-label={`Assessment: ${plain.recommendation}`}
                >
                  <strong>{plain.recommendation}</strong>
                </p>
                <p data-testid="acceptance-review-plain-why">{plain.why}</p>
                <h6>Completed or partially shown</h6>
                <ul data-testid="acceptance-review-proven">
                  {plain.looksGood.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <h6>Missing</h6>
                <ul data-testid="acceptance-review-needs-attention">
                  {(plain.needsAttention.length
                    ? plain.needsAttention
                    : ["Nothing is marked missing beyond the assessment above."]
                  ).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <h6>Could not verify</h6>
                <ul data-testid="acceptance-review-could-not-check">
                  {(plain.couldNotCheck.length
                    ? plain.couldNotCheck
                    : ["No unverifiable items were recorded."]
                  ).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p data-testid="acceptance-review-plain-next">
                  <strong>What to do next:</strong> {plain.next}
                </p>
                {reviews.length > 1 && reviews[0] && reviews[1] ? (
                  <div
                    className="acceptance-review-pair-compare"
                    data-testid="acceptance-review-pair-compare"
                  >
                    <h6>Delivery comparison</h6>
                    <p className="acceptance-review-muted">
                      Same Task and frozen requirements. Only the delivery revision and evidence
                      changed.
                    </p>
                    <table>
                      <thead>
                        <tr>
                          <th>Requirement</th>
                          <th>Earlier delivery</th>
                          <th>Later delivery</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(
                          reviews[1]?.founderPresentation?.criterionCards ||
                          reviews[1]?.criteriaEvaluation?.results ||
                          []
                        ).map((earlier, index) => {
                          const laterCard =
                            reviews[0]?.founderPresentation?.criterionCards?.[index] ||
                            reviews[0]?.criteriaEvaluation?.results?.[index];
                          const requirement =
                            earlier.requirement ||
                            earlier.criterionText ||
                            laterCard?.requirement ||
                            laterCard?.criterionText ||
                            `Requirement ${index + 1}`;
                          return (
                            <tr key={`${requirement}-${index}`}>
                              <td>{requirement}</td>
                              <td>{earlier.resultLabel || earlier.status || "—"}</td>
                              <td>{laterCard?.resultLabel || laterCard?.status || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {Array.isArray(latest.founderPresentation?.criterionCards) &&
                latest.founderPresentation.criterionCards.length ? (
                  <section
                    className="acceptance-review-evidence-cards"
                    data-testid="acceptance-review-evidence-cards"
                    aria-label="Requirement evidence"
                  >
                    <h6>Requirement evidence</h6>
                    {latest.founderPresentation.criterionCards.map((card) => (
                      <FounderEvidenceCard
                        key={`${card.criterionIndex}-${card.requirement}`}
                        card={card}
                      />
                    ))}
                  </section>
                ) : null}
              </div>
            );
          })()}
          <details className="acceptance-review-details">
            <summary>Reviewer notes</summary>
            <HandoverReport handover={latest.handover} />
          </details>
          <details className="acceptance-review-details" data-testid="acceptance-review-technical-details">
            <summary>Technical details</summary>
          <dl className="acceptance-review-meta">
            <div>
              <dt>Branch:</dt>
              <dd>{latest.reviewSource?.branchName}</dd>
            </div>
            <div>
              <dt>Base branch:</dt>
              <dd>{latest.reviewSource?.baseRef}</dd>
            </div>
            <div>
              <dt>Head commit:</dt>
              <dd>{shortSha(latest.reviewSource?.headCommitSha)}</dd>
            </div>
            {latest.evidenceRepoId && latest.evidenceRepoId !== latest.repoId && (
              <div>
                <dt>Evidence source:</dt>
                <dd>Branch-specific analysis <code>{shortSha(latest.evidenceRepoId)}</code></dd>
              </div>
            )}
            <div>
              <dt>Criteria source:</dt>
              <dd data-testid="acceptance-review-criteria-source">
                {latest.criteriaSource === "intent_contract_tip"
                  ? "Frozen Task"
                  : "Task snapshot"}
              </dd>
            </div>
            <div>
              <dt>Flow state:</dt>
              <dd data-testid="acceptance-review-flow-state">
                {latest.flow?.state || latest.flowState || latest.status}
              </dd>
            </div>
            <div>
              <dt>Verdict:</dt>
              <dd data-testid="acceptance-review-verdict">{latest.reviewVerdict}</dd>
            </div>
            <div>
              <dt>Stale:</dt>
              <dd data-testid="acceptance-review-stale">{latest.staleState}</dd>
            </div>
            <div>
              <dt>Created:</dt>
              <dd>{formatDate(latest.createdAt)}</dd>
            </div>
            {aggregate ? (
              <div>
                <dt>Results:</dt>
                <dd data-testid="acceptance-review-aggregate">
                  Met {aggregate.met} &middot; Partial {aggregate.partiallyMet} &middot; Cannot verify{" "}
                  {aggregate.cannotVerify} &middot; Not met {aggregate.notMet}
                </dd>
              </div>
            ) : null}
            {latest.intentAlignment?.vision ? (
              <div>
                <dt>Product vision:</dt>
                <dd data-testid="acceptance-review-vision-alignment">
                  {latest.intentAlignment.vision.alignment.replace(/_/g, " ")}
                </dd>
              </div>
            ) : null}
            {latest.intentAlignment?.technical ? (
              <div>
                <dt>Technical delivery:</dt>
                <dd data-testid="acceptance-review-technical-alignment">
                  {latest.intentAlignment.technical.alignment.replace(/_/g, " ")}
                </dd>
              </div>
            ) : null}
          </dl>
          </details>

          <details className="acceptance-review-details" data-testid="acceptance-review-evidence-details">
            <summary>See detailed evidence</summary>
          {latest.checkContract ? (
            <section
              className="acceptance-review-check"
              data-testid="acceptance-review-check"
              aria-labelledby="acceptance-review-check-heading"
            >
              <h6 id="acceptance-review-check-heading">Release verification</h6>
              <p
                className={`acceptance-review-check-state acceptance-review-check-state--${
                  latest.checkContract.conclusion || latest.checkContract.status
                }`}
                data-testid="acceptance-review-check-state"
              >
                <strong>{latest.checkContract.name}:</strong>{" "}
                {latest.checkContract.conclusion === "success"
                  ? "would pass"
                  : latest.checkContract.conclusion
                    ? `would not pass (${latest.checkContract.conclusion.replace(/_/g, " ")})`
                    : `not concluded (${latest.checkContract.status.replace(/_/g, " ")})`}
              </p>
              <p data-testid="acceptance-review-check-summary">{latest.checkContract.summary}</p>
              <p data-testid="acceptance-review-ci-evidence">
                <strong>Automated tests:</strong> {describeCiEvidence(latest.ciEvidence)}
              </p>
              <p className="acceptance-review-check-note" data-testid="acceptance-review-ci-freeze-note">
                Test results are frozen when the review is created. Re-run the review to pick up
                later results.
              </p>
              <p className="acceptance-review-check-note">
                {latest.checkPublication?.state === "published"
                  ? "Published to the repository as a check run."
                  : latest.checkPublication?.state === "failed"
                    ? "Publication to the repository failed; this result is not visible on the pull request."
                    : "Not published to the repository. This is DataFlow's own assessment, not a merge gate."}
              </p>
            </section>
          ) : null}

          <RequirementsPanel
            trace={latest.requirementTrace}
            coverage={latest.requirementCoverage}
            routing={latest.feedbackRouting}
            divergence={latest.handoffDivergence}
          />

          {latest.intentAlignment ? (
            <div className="acceptance-review-intent" data-testid="acceptance-review-intent">
              <h6>Intent alignment</h6>
              <p data-testid="acceptance-review-vision-summary">
                {latest.intentAlignment.vision?.summary}
              </p>
              <p data-testid="acceptance-review-technical-summary">
                {latest.intentAlignment.technical?.summary}
              </p>
              {latest.intentAlignment.scope && !latest.intentAlignment.scope.intact ? (
                <p className="acceptance-review-stale-banner" data-testid="acceptance-review-scope-banner">
                  Scope is not intact: {(latest.intentAlignment.scope.violations || []).join(", ")}
                </p>
              ) : null}
              {Array.isArray(latest.intentAlignment.findings) && latest.intentAlignment.findings.length > 0 ? (
                <ul data-testid="acceptance-review-intent-findings">
                  {latest.intentAlignment.findings.map((finding) => (
                    <li key={finding.angle}>
                      <strong>{finding.angle.replace(/_/g, " ")}</strong> ({finding.status}): {finding.summary}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          </details>

          {latest.staleState && latest.staleState !== "fresh" && (
            <p
              className="acceptance-review-stale-banner"
              role="status"
              data-testid="acceptance-review-stale-banner"
            >
              This review is out of date. The delivered code changed. Review the current version
              before making a decision.
            </p>
          )}

          {latest.conditionsSummary?.state &&
          latest.conditionsSummary.state !== "not_applicable" ? (
            <div
              className="acceptance-review-conditions"
              data-testid="acceptance-review-conditions"
            >
              <h6>Acceptance conditions</h6>
              <p
                className={`acceptance-review-conditions-state acceptance-review-conditions-state-${latest.conditionsSummary.state}`}
                data-testid="acceptance-review-conditions-state"
              >
                {latest.conditionsSummary.acceptanceEffective
                  ? "All follow-ups are done. Release checks outside this review are still required before shipping."
                  : latest.conditionsSummary.state === "unstructured"
                    ? "This acceptance used free-text conditions that are not tracked. Re-decide with owned follow-ups."
                    : latest.conditionsSummary.state === "expired"
                      ? "A follow-up passed its due date without being finished. This acceptance is no longer usable until the owner resolves it or you request a new review."
                      : latest.conditionsSummary.state === "rejected"
                        ? "A follow-up was rejected. This acceptance no longer holds."
                        : "Follow-ups are still open. This is not yet a usable acceptance."}
              </p>
              <ul>
                {(latest.conditions || []).map((condition) => (
                  <li
                    key={condition.conditionId}
                    data-testid={`acceptance-review-condition-${condition.conditionId}`}
                  >
                    <span className="acceptance-review-condition-description">
                      {condition.description}
                    </span>
                    <span
                      className={`acceptance-review-condition-status acceptance-review-condition-status-${condition.status}`}
                    >
                      {condition.status === "expired"
                        ? "Past due"
                        : condition.status === "open"
                          ? "Open"
                          : condition.status === "satisfied"
                            ? "Done"
                            : condition.status === "waived"
                              ? "Waived"
                              : condition.status === "rejected"
                                ? "Rejected"
                                : condition.status}
                    </span>
                    {condition.owner ? (
                      <span className="acceptance-review-condition-owner">
                        Owner: {condition.owner}
                      </span>
                    ) : null}
                    {condition.dueAt ? (
                      <span className="acceptance-review-condition-due">
                        Due {new Date(condition.dueAt).toLocaleDateString()}
                      </span>
                    ) : null}
                    {condition.status === "expired" ? (
                      <p
                        className="acceptance-review-condition-expired-guidance"
                        data-testid={`acceptance-review-condition-expired-${condition.conditionId}`}
                      >
                        {condition.owner
                          ? `${condition.owner} must mark this done, waive it with a reason, or reject it. Until then, do not treat this work as accepted.`
                          : "An owner must mark this done, waive it with a reason, or reject it. Until then, do not treat this work as accepted."}
                      </p>
                    ) : null}
                    {condition.requiredEvidence ? (
                      <span className="acceptance-review-condition-evidence">
                        Evidence: {condition.requiredEvidence}
                      </span>
                    ) : null}
                    {mutateAllowed && condition.status === "open" ? (
                      <span className="acceptance-review-condition-actions">
                        <label>
                          Resolution note
                          <input
                            type="text"
                            value={conditionNotes[condition.conditionId] || ""}
                            onChange={(e) =>
                              setConditionNotes((prev) => ({
                                ...prev,
                                [condition.conditionId]: e.target.value,
                              }))
                            }
                            disabled={deciding}
                            data-testid={`acceptance-review-condition-note-${condition.conditionId}`}
                          />
                        </label>
                        <button
                          type="button"
                          className="secondary-button"
                          disabled={deciding}
                          onClick={() =>
                            handleResolveCondition(condition.conditionId, "satisfied")
                          }
                          data-testid={`acceptance-review-condition-satisfy-${condition.conditionId}`}
                        >
                          Mark satisfied
                        </button>
                        <button
                          type="button"
                          className="secondary-button"
                          disabled={deciding}
                          onClick={() => handleResolveCondition(condition.conditionId, "waived")}
                          data-testid={`acceptance-review-condition-waive-${condition.conditionId}`}
                        >
                          Waive (admin)
                        </button>
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {mutateAllowed && latest.status === "completed" && (
            <div
              className="acceptance-review-decision"
              data-testid="acceptance-review-decision"
              tabIndex={-1}
              ref={decisionRegionRef}
              aria-labelledby="acceptance-review-decision-heading"
            >
              <h6 id="acceptance-review-decision-heading" className="acceptance-review-visually-hidden">
                Record a human decision
              </h6>
              {decisionAllowed === false ||
              (!(latest.flow?.allowedEvents || []).includes("HUMAN_ACCEPT") &&
                (latest.checkContract?.conclusion === "awaiting_human_decision" ||
                  latest.flowState === "awaiting_human_decision" ||
                  latest.flow?.state === "awaiting_human_decision")) ? (
                <p data-testid="acceptance-review-decision-blocked">
                  You cannot make a decision yet.
                </p>
              ) : (
                <>
              {(() => {
                const allowed = latest.flow?.allowedEvents || [];
                const canApprove = allowed.includes("HUMAN_ACCEPT");
                const canConditional =
                  allowed.includes("HUMAN_ACCEPT_CONDITIONAL") &&
                  latest.handover?.canApproveConditionally !== false;
                const canReject = allowed.includes("HUMAN_REJECT");
                const canRerun = allowed.includes("RERUN_REQUESTED");
                const recommended = latest.handover?.recommendedEvent;
                return (
                  <>
              {!canApprove && canReject ? (
                <p data-testid="acceptance-review-decision-blocked">
                  {latest.handover?.recommendation === "do_not_approve"
                    ? "Changes are required. Request changes, or review again after a fix."
                    : "You can’t approve this yet."}
                </p>
              ) : (
                <p data-testid="acceptance-review-decision-allowed">Choose a decision.</p>
              )}
              {latest.handover?.headline ? (
                <p className="acceptance-review-handover-decision-hint" data-testid="acceptance-review-handover-decision-hint">
                  Recommendation: {latest.handover.headline}
                </p>
              ) : null}
              {canConditional ? (
                <>
              <label>
                Decision note (required to approve with conditions)
                <textarea
                  value={decisionNote}
                  onChange={(e) => setDecisionNote(e.target.value)}
                  rows={2}
                  disabled={deciding}
                  data-testid="acceptance-review-decision-note"
                />
              </label>
              <label>
                Conditions (one per line: description | owner | due YYYY-MM-DD)
                <textarea
                  value={conditionsDraft}
                  onChange={(e) => setConditionsDraft(e.target.value)}
                  rows={3}
                  disabled={deciding}
                  placeholder="Add a rollback test | platform-team | 2099-03-01"
                  data-testid="acceptance-review-conditions-draft"
                />
              </label>
                </>
              ) : null}
              <div className="acceptance-review-decision-actions">
                {canApprove ? (
                <button
                  type="button"
                  className={
                    recommended === "HUMAN_ACCEPT" ? "primary-button" : "secondary-button"
                  }
                  disabled={deciding}
                  onClick={() => handleDecision("HUMAN_ACCEPT")}
                  data-testid="acceptance-review-accept"
                >
                  Approve this delivery
                </button>
                ) : null}
                {canConditional ? (
                <button
                  type="button"
                  className={
                    recommended === "HUMAN_ACCEPT_CONDITIONAL"
                      ? "primary-button"
                      : "secondary-button"
                  }
                  disabled={deciding}
                  onClick={() => handleDecision("HUMAN_ACCEPT_CONDITIONAL")}
                  data-testid="acceptance-review-accept-conditional"
                >
                  Approve this delivery with conditions
                </button>
                ) : null}
                {canReject ? (
                <button
                  type="button"
                  className={
                    recommended === "HUMAN_REJECT" || !canApprove
                      ? "primary-button"
                      : "secondary-button"
                  }
                  disabled={deciding}
                  onClick={() => handleDecision("HUMAN_REJECT")}
                  data-testid="acceptance-review-reject"
                >
                  Request changes to this delivery
                </button>
                ) : null}
                {canRerun ? (
                <button
                  type="button"
                  className={
                    recommended === "RERUN_REQUESTED" ? "primary-button" : "secondary-button"
                  }
                  disabled={deciding}
                  onClick={() => handleLifecycle("RERUN_REQUESTED")}
                  data-testid="acceptance-review-rerun"
                >
                  Review the work again
                </button>
                ) : null}
              </div>
                  </>
                );
              })()}
                </>
              )}
            </div>
          )}

          {latest.decidedAt || latest.frozenHandover ? (
            <section
              className="acceptance-review-export"
              data-testid="acceptance-review-export"
              aria-labelledby="acceptance-review-export-heading"
            >
              <h6 id="acceptance-review-export-heading">Evidence pack</h6>
              <button
                type="button"
                className="secondary-button"
                onClick={handleExport}
                disabled={exporting}
                data-testid="acceptance-review-export-button"
              >
                {exporting ? "Preparing…" : "Export decided evidence pack"}
              </button>
              <p role="status" data-testid="acceptance-review-export-status">
                {exportError
                  ? exportError
                  : exportResult
                    ? `Exported ${exportResult.integrity?.canonicalBytes ?? 0} bytes. Integrity ${exportResult.integrity?.algorithm ?? "sha256"}: ${exportResult.integrity?.hash ?? ""}`
                    : "Exports the frozen evidence and handover the reviewer decided against, with an integrity hash."}
              </p>
            </section>
          ) : null}

          {qualityWarnings.length > 0 && (
            <div
              className="acceptance-review-quality-warning"
              role="status"
              data-testid="acceptance-review-quality-warning"
            >
              {qualityWarnings.map((warning) => (
                <p key={warning}>{warning}</p>
              ))}
            </div>
          )}

          {latest.status === "failed" ? (
            <p className="acceptance-review-failed" data-testid="acceptance-review-failed">
              {latest.errorMessage ||
                "Acceptance Review could not be completed. Try again after repo indexing finishes."}
            </p>
          ) : (
            <>
              {criteria.length > 0 ? (
                <div
                  className="acceptance-review-criterion-list"
                  data-testid="acceptance-review-criterion-list"
                >
                  <dl className="task-overview-score" data-testid="acceptance-review-scorecard">
                    <div>
                      <dt>Looks supported</dt>
                      <dd>{scorecard.supported}</dd>
                    </div>
                    <div>
                      <dt>Needs attention</dt>
                      <dd>{scorecard.partial}</dd>
                    </div>
                    <div>
                      <dt>Needs proof</dt>
                      <dd>{scorecard.unverifiable}</dd>
                    </div>
                    <div>
                      <dt>Not supported</dt>
                      <dd>{scorecard.unsupported}</dd>
                    </div>
                  </dl>
                  {groupedCriteria.map((group) => (
                    <section key={group.id} data-testid={`acceptance-review-group-${group.id}`}>
                      <details>
                      <summary>
                        {group.label} — {group.items.length} requirement
                        {group.items.length === 1 ? "" : "s"}
                      </summary>
                      {group.items.map((result) => (
                        <CriterionCard
                          key={`${result.criterionIndex}-${result.criterionHash}`}
                          result={result}
                        />
                      ))}
                      </details>
                    </section>
                  ))}
                </div>
              ) : (
                <p className="acceptance-review-muted">
                  Criterion evaluation is not available on this review yet.
                </p>
              )}

              <details className="acceptance-review-details">
                <summary>Branch evidence and factual comparison</summary>
                <dl className="acceptance-review-meta">
                  <div>
                    <dt>Changed files:</dt>
                    <dd>{latest.branchEvidence?.totalChangedFiles ?? 0}</dd>
                  </div>
                  <div>
                    <dt>Additions / deletions:</dt>
                    <dd>
                      {latest.branchEvidence?.additions ?? 0} /{" "}
                      {latest.branchEvidence?.deletions ?? 0}
                    </dd>
                  </div>
                  <div>
                    <dt>Expected files touched:</dt>
                    <dd>{latest.factualComparison?.expectedFilesTouched?.length ?? 0}</dd>
                  </div>
                  <div>
                    <dt>Excluded files touched:</dt>
                    <dd>{latest.factualComparison?.excludedFilesTouched?.length ?? 0}</dd>
                  </div>
                </dl>
                <FileListDetails
                  label="Expected files touched"
                  files={latest.factualComparison?.expectedFilesTouched}
                />
                <FileListDetails
                  label="Excluded files touched"
                  files={latest.factualComparison?.excludedFilesTouched}
                />
                <FileListDetails
                  label="Unexpected implementation files touched"
                  files={latest.factualComparison?.unexpectedFilesTouched}
                />
              </details>
            </>
          )}
        </div>
      )}

    </section>
  );
}
